import React, { useContext, useMemo, useState } from 'react';
import OrderDetailsStepper from '../../../../../../components/OrderDetailsStepper/OrderDetailsStepper';
import FulfilmentSchedule from '../../AdditionalDetails/FulfilmentSchedule';
import PickUpDeliveryDetails from '../../AdditionalDetails/PickUp&DeliveryDetails';
import BaseOrderDetails from '../../../../../../components/BaseOrderDetails/BaseOrderDetails';
import { AdminOrderProps } from '../../../adminOrder.interface';
import PaymentTerms from '../../AdditionalDetails/PaymentTerms';
import { PaymentNotesStatusesEnum } from '../../../../../../services/billing.service';
import { OrderDetailsContext } from '../../../../../../context/OrderDetailsContext/order-details.context';
import OrderDetailsInvoice from '../../../../../../components/OrderDetailsInvoice/OrderDetailsInvoice';
import { OrderTarget } from '../../../../../../components/OrdersTable/interfaces/OrderStatus.interface';
import OrderContractDetails from '../../../../../../components/OrderContractDetails/OrderContractDetails';

const Delivered: React.FC<AdminOrderProps> = (props) => {
	const { selectedOrder, contract } = props;
	const { producerInvoice, carrierInvoice, customerInvoice, actionFor } =
		useContext(OrderDetailsContext);
	const { deliveryIncluded } = selectedOrder!;
	const [stepperMargin, setStepperMargin] = useState<number>(0);

	const simpleOrder = useMemo(() => {
		return selectedOrder?.trucks?.length === 1;
	}, [selectedOrder?.trucks]);

	const currentInvoice = useMemo(() => {
		const invoices = {
			[OrderTarget.producer]: producerInvoice,
			[OrderTarget.customer]: customerInvoice,
			[OrderTarget.transporter]: carrierInvoice,
		};
		return invoices[actionFor as keyof typeof invoices] ?? null;
	}, [actionFor, producerInvoice, customerInvoice, carrierInvoice]);

	const currentStep = useMemo(() => {
		const baseStep =
			simpleOrder && deliveryIncluded
				? 12
				: simpleOrder && !deliveryIncluded
				? 6
				: 5;

		if (
			[PaymentNotesStatusesEnum.PENDING_INVOICE, 'pending'].includes(
				currentInvoice?.status as PaymentNotesStatusesEnum,
			)
		) {
			return baseStep - 1;
		}

		if (currentInvoice?.status === PaymentNotesStatusesEnum.UPLOADED_INVOICE) {
			return baseStep;
		}

		if (currentInvoice?.status === PaymentNotesStatusesEnum.INVOICE_CONFIRMED) {
			return baseStep + 1;
		}

		if (currentInvoice?.status === PaymentNotesStatusesEnum.PAID) {
			return baseStep;
		}

		return baseStep;
	}, [currentInvoice, simpleOrder, deliveryIncluded]);

	const handleWidthChange = (value: number) => {
		setStepperMargin(value);
	};

	const hasInvoiceBlock =
		actionFor !== OrderTarget.customer &&
		[
			PaymentNotesStatusesEnum.UPLOADED_INVOICE,
			PaymentNotesStatusesEnum.INVOICE_CONFIRMED,
		].includes(currentInvoice?.status as PaymentNotesStatusesEnum);

	const renderInitialSection = () =>
		hasInvoiceBlock ? (
			<OrderDetailsInvoice
				currentStatus={currentInvoice?.status!}
				target={OrderTarget.admin}
				order={selectedOrder}
			/>
		) : null;

	return (
		<>
			<div style={{ marginBottom: `${stepperMargin}px` }}>
				<OrderDetailsStepper
					current={currentStep}
					simpleOrder={simpleOrder}
					isFinalStep={
						currentInvoice
							? currentInvoice?.status === PaymentNotesStatusesEnum.PAID
							: true
					}
					order={selectedOrder}
					onWidthChange={handleWidthChange}
				/>
			</div>

			{contract ? (
				<div className="px-10 pt-7 pb-3.5">
					<OrderContractDetails
						selectedOrder={selectedOrder}
						contract={contract}
						target="admin"
					/>
				</div>
			) : hasInvoiceBlock ? (
				<div className="px-10 pt-7 pb-3.5">{renderInitialSection()}</div>
			) : null}

			{contract && hasInvoiceBlock ? (
				<div className="px-10 py-3.5">{renderInitialSection()}</div>
			) : null}

			<div
				className={`px-10 ${
					contract || hasInvoiceBlock ? 'py-3.5' : 'pt-7 pb-3.5'
				}`}
			>
				<BaseOrderDetails
					selectedOrder={selectedOrder}
					isOpen={true}
					simpleOrder={simpleOrder}
					deliveryIncluded={deliveryIncluded!}
					target={selectedOrder?.target!}
					contract={contract}
				/>
			</div>

			<div className="px-10 py-3.5">
				<PaymentTerms selectedOrder={selectedOrder} forSeller={true} />
			</div>

			{deliveryIncluded && (
				<div className="px-10 py-3.5">
					<PaymentTerms selectedOrder={selectedOrder} forSeller={false} />
				</div>
			)}

			<div className="px-10 py-3.5">
				<PickUpDeliveryDetails
					selectedOrder={selectedOrder}
					target={selectedOrder?.target}
					isPickUp={true}
					simpleOrder={simpleOrder}
				/>
			</div>

			<div className="px-10 py-3.5">
				{deliveryIncluded ? (
					<PickUpDeliveryDetails
						selectedOrder={selectedOrder}
						target={selectedOrder?.target}
						isPickUp={false}
						simpleOrder={simpleOrder}
					/>
				) : (
					<FulfilmentSchedule
						selectedOrder={selectedOrder}
						step={
							simpleOrder && deliveryIncluded ? 12 : !deliveryIncluded ? 6 : 5
						}
						deliveryIncluded={deliveryIncluded!}
						simpleOrder={simpleOrder}
					/>
				)}
			</div>

			<div className="px-10 pb-7 pt-3.5">
				{deliveryIncluded ? (
					<FulfilmentSchedule
						selectedOrder={selectedOrder}
						step={
							simpleOrder && deliveryIncluded ? 12 : !deliveryIncluded ? 6 : 5
						}
						deliveryIncluded={deliveryIncluded!}
						simpleOrder={simpleOrder}
					/>
				) : (
					<PickUpDeliveryDetails
						selectedOrder={selectedOrder}
						target={selectedOrder?.target}
						isPickUp={false}
						simpleOrder={simpleOrder}
					/>
				)}
			</div>
		</>
	);
};

export default Delivered;
